import { FormEvent, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Typography, Alert, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import IconButton from "../../components/ui/IconButton";
import LoadingButton from "../../components/ui/LoadingButton";
import Input from "../../components/ui/Input";
import { PasswordCriteria } from "../../components/PasswordCriteria";
import { useIntl } from "../../contexts/IntlContext";
import { LayoutUnauthenticated } from "../../components/LayoutUnauthenticated";
import api from "../../services/api";
import { PasswordValidator } from "../../utils/passwordValidator";

export function UserRegisterAccept() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordRules, setPasswordRules] = useState<any[]>([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenChecking, setTokenChecking] = useState(true);
  const [formErrors, setFormErrors] = useState({
    password: "",
    confirmPassword: "",
  });

  const navigate = useNavigate();
  const { hash } = useParams();
  const intl = useIntl();

  useEffect(() => {
    if (!hash) {
      setTokenChecking(false);
      return;
    }
    api
      .get(`app/auth/token/validate/${hash}?type=register`)
      .catch((error: any) => {
        if (error.response?.data?.message) {
          setMessage(error.response.data.message);
        } else {
          setMessage(
            intl.formatMessage({ id: "userRegisterAccept.invalidToken" }),
          );
        }
        setMessageType("error");
      })
      .finally(() => setTokenChecking(false));
  }, [hash, intl]);

  useEffect(() => {
    if (messageType !== "success") return;
    const timer = setTimeout(() => navigate("/login"), 3000);
    return () => clearTimeout(timer);
  }, [messageType, navigate]);

  useEffect(() => {
    if (!password && !confirmPassword) {
      setPasswordRules([]);
      return;
    }
    setPasswordRules(
      PasswordValidator.validateWithConfirm(password, confirmPassword),
    );
  }, [password, confirmPassword]);

  const validateForm = () => {
    let isValid = true;
    const errors = { password: "", confirmPassword: "" };

    const passwordValidation = PasswordValidator.validateWithConfirm(
      password,
      confirmPassword,
    );
    const strengthFailed = passwordValidation.some((rule) => rule.name !== "confirm" && rule.error);
    const confirmFailed = passwordValidation.some((rule) => rule.name === "confirm" && rule.error);
    if (strengthFailed) {
      errors.password = intl.formatMessage({
        id: "validation.passwordCriteria",
      });
      isValid = false;
    }
    if (confirmFailed) {
      errors.confirmPassword = intl.formatMessage({
        id: "validation.passwordMismatch",
      });
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await api.put(`app/auth/register/accept/${hash}`, {
        password,
        confirmPassword,
      });

      setMessage(intl.formatMessage({ id: "userRegisterAccept.success" }));
      setMessageType("success");
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: "userRegisterAccept.error" }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutUnauthenticated
      title={intl.formatMessage({ id: "userRegisterAccept.title" })}
      subtitle={intl.formatMessage({ id: "userRegisterAccept.subtitle" })}
    >
      {!message && tokenChecking ? (
        <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
          {intl.formatMessage({ id: "resetPassword.checkingToken" })}
        </Alert>
      ) : !message ? (
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Typography variant="h6">
            {intl.formatMessage({ id: "userRegisterAccept.definePassword" })}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {intl.formatMessage({ id: "userRegisterAccept.description" })}
          </Typography>

          <Input
            label={intl.formatMessage({ id: "userRegisterAccept.newPassword" })}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={formErrors.password !== ""}
            helperText={formErrors.password}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        tooltip={intl.formatMessage({ id: "login.togglePasswordVisibility" })}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                  </InputAdornment>
                ),
              },
              formHelperText: { sx: { minHeight: '1.5em' } },
            }}
            sx={{ width: "100%", mb: 2 }}
          />

          <Input
            label={intl.formatMessage({
              id: "userRegisterAccept.confirmPassword",
            })}
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            error={formErrors.confirmPassword !== ""}
            helperText={formErrors.confirmPassword}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        edge="end"
                        tooltip={intl.formatMessage({ id: "login.togglePasswordVisibility" })}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                  </InputAdornment>
                ),
              },
              formHelperText: { sx: { minHeight: '1.5em' } },
            }}
            sx={{ width: "100%", mb: 2 }}
          />

          <PasswordCriteria passwordRules={passwordRules} />

          <LoadingButton
            type="submit"
            color="primary"
            loading={loading}
            sx={{ width: "100%", mb: 2 }}
          >
            {intl.formatMessage({ id: "userRegisterAccept.submit" })}
          </LoadingButton>
        </form>
      ) : (
        <Alert severity={messageType === "error" ? "error" : "success"} sx={{ mb: 2 }}>
          {message}
          {messageType === "success"
            ? <Typography variant="body2" sx={{ mt: 1 }}>{intl.formatMessage({ id: "userRegisterAccept.redirectHint" })}</Typography>
            : null}
        </Alert>
      )}
      <LoadingButton
        color="secondary"
        onClick={() => navigate("/login")}
        sx={{ width: "100%", mb: 2 }}
      >
        {intl.formatMessage({ id: "userRegisterAccept.backToLogin" })}
      </LoadingButton>
    </LayoutUnauthenticated>
  );
}
